import { memo, useMemo, useRef, forwardRef } from "react";
import { arrayOf, bool, number, string, node, oneOfType, oneOf, func, shape, object } from "prop-types";
import { STICKY_LEFT_CLASS } from "@utils";
import { useEstimateEditorSettings } from "@tools";
import Grid from "@material-ui/core/Grid";
import { capitalize } from "@material-ui/core";
import { CellRescale } from "./components";
import useCellStyle from "../../../../styles/CommonTableCellStyles.style";
import useStyle from "./PinnableCell.style";
import classnames from "classnames";

export const PinnableCell = memo(forwardRef(({
  children,
  actions,
  pinnedLeft,
  colName,
  stretchToChildrenWidth,
  allowOverflowDisplay,
  highlightCellText,
  className,
  innerClassName,
  isHeader,
  allowEdition,
  columnSettings,
  boldText,
  justifyContent,
  resizeable,
  wider,
  keepMinSize,
  keepSmallestSize,
  containers,
  innerStyle,
  alignItems,
  hasStaticWidth,
  widerPaddings,
  pinActionsLeft,
  actionsContainerWidth,
  tableDepth = 0,
  size="large",
  wrap="nowrap",
}, ref) => {
  const { isPdfPrinterView } = useEstimateEditorSettings();
  const classes = useStyle({ colName, tableDepth });
  const cellClasses = useCellStyle();
  
  const cellRef = ref || useRef(null);
  
  const allowRescale = useMemo(() => (
    isHeader && allowEdition && Boolean(columnSettings)
  ), [isHeader, allowEdition, columnSettings]);
  
  const showActionsContainer = useMemo(() => (
    (Boolean(actions) || allowRescale)
  ), [actions, allowRescale]);

  return (
    <Grid
      ref={cellRef}
      item
      container
      role="rowitem"
      name={colName}
      alignItems={alignItems || (isHeader ? "flex-end" : "flex-start")}
      className={classnames(
        "cell",
        cellClasses.cell,
        classes.root,
        hasStaticWidth && classes.staticWidth,
        resizeable && classes.resizeable,
        resizeable && !isPdfPrinterView && classes.pinnedLeft,
        boldText && cellClasses.bolder,
        pinnedLeft && !isPdfPrinterView && classes.isSticky,
        pinnedLeft && !isPdfPrinterView && STICKY_LEFT_CLASS,
        wider && classes.wider,
        keepSmallestSize && classes.keepSmallestSize,
        highlightCellText && "color-primary",
        isHeader && cellClasses.headCell,
        stretchToChildrenWidth && "w-max",
        widerPaddings && "px-1-5",
        (!allowEdition || !isHeader) && allowOverflowDisplay && "display-overflow",
        cellClasses[`size${capitalize(size)}`],
        className
      )}
    >
      { containers }
      <Grid
        item container
        justifyContent={justifyContent || (showActionsContainer ? "space-between" : "flex-start")}
        alignItems="center"
        wrap={wrap}
        className={classnames(
          // !isHeader && cellClasses.cellInner,
          !isHeader && cellClasses[`sizeInner${capitalize(size)}`],
          keepMinSize && (
            resizeable ? classes.resizeableKeepMinSize : classes.keepMinSize
          ),
          innerClassName
        )}
        style={innerStyle}
      >
        {children}
        {
          showActionsContainer &&
          <Grid
            item
            container
            wrap="nowrap"
            alignItems="center"
            justifyContent={pinActionsLeft ? "flex-start" : "flex-end"}
            className={classnames(
              "opaque ml-2",
              !actionsContainerWidth && "w-max",
              // allowRescale && "relative",
              isHeader ? cellClasses.headerActionWrapper : cellClasses.actionWrapper
            )}
            style={{ width: actionsContainerWidth, minWidth: actionsContainerWidth }}
          >
            {actions}
            {
              allowRescale &&
              <CellRescale
                colName={colName}
                cellRef={cellRef}
                columnSettings={columnSettings}
                widerPaddings={widerPaddings}
              />
            }
          </Grid>
        }
      </Grid>
    </Grid>
  );
}));

PinnableCell.propTypes = {
  children: oneOfType([node, string]),
  containers: node,
  actions: oneOfType([node, arrayOf(node)]),
  colName: string,
  tableDepth: number,
  
  pinnedLeft: bool,
  
  size: oneOf(["xSmall", "small", "medium", "large", "xLarge", "2XLarge"]),
  alignItems: oneOf(["center", "flex-start", "flex-end"]),
  
  name: string,
  stretchToChildrenWidth: bool,
  allowOverflowDisplay: bool,
  highlightCellText: bool,
  className: string,
  innerClassName: string,
  innerStyle: object,
  isHeader: bool,
  allowEdition: bool,
  boldText: bool,
  columnSettings: object,
  justifyContent: string,
  resizeable: bool,
  keepMinSize: bool,
  keepSmallestSize: bool,
  hasStaticWidth: bool,
  wrap: oneOf(["wrap", "nowrap"]),
  widerPaddings: bool,
  pinActionsLeft: bool,
  actionsContainerWidth: number,
  expandContainerProps: shape({
    expandable: bool,
    expanded: bool,
    noArrow: bool,
    onExpand: func,
  })
};
